/*
 * <twc-pdp-buybox> — the PDP buybox island.
 *
 * The section renders the WHOLE buybox in Liquid: gallery, panel, the state
 * CTA and the tasting tabs all arrive complete and correct for a guest, and
 * for a member on every state the engine has no opinion about. This element
 * only adds the three things Liquid cannot do:
 *
 *   1. tabs      switch COLOUR / NOSE / PALATE (click + arrow keys)
 *   2. gallery   swap the hero when a thumbnail is chosen
 *   3. member    resolve the real CTA from the shared store
 *
 * There is NO cart/checkout path here: guests
 * never see a live button (their anchors go to the join page, rendered by
 * Liquid), and a member acts on their BOX through the engine — add-in, top-up,
 * ballot, waitlist — never on a Shopify cart.
 *
 * (3) runs ONLY when the section says so (data-engine="1" — a member on an
 * actionable state). The Liquid renders that button DISABLED; this island
 * enables it once the store answers. Sub-status is checked BEFORE grid
 * resolution so a PDP click can never reactivate a paused or cancelled
 * member: a cancelled member gets a rejoin link, a
 * payment-failed member a fix-payment link, and a paused member keeps a live
 * button whose writes are gated through the shared resume dialog
 * (assets/twc-resume-dialog.js — confirm re-issues the same mutation with
 * confirmResume:true).
 *
 * THE RESOLVER lives in assets/twc-cta-resolver.js — memberGate() for the
 * sub-status ladder, resolveCta() for the grid scan, presentation() for the
 * kind -> button shape and dispatch() for the write that the button makes.
 * This file keeps every DOM write and every word, and it is a HOST of that
 * module: twcBusy() / twcError() / twcText() plus the _abort / _dialog /
 * _opener slots of the resume dialog. The product cards use the same four
 * functions, thus a card and its whisky page send the same request. The
 * rules: ballots are first-class grid entries, no special-case. Find this
 * page's catalogue item id, then scan the server-composed grids for the entry
 * that carries this product's CTA. Ballot-kind entries win regardless of
 * section visibility (a ballot's own window governs); every other kind needs
 * its section visible. No match = the engine says this whisky is not
 * actionable — "Not available right now", enforced server-side anyway.
 *
 * Data flow is the house rule: subscribe to TwcMemberData, never GET an
 * endpoint directly. Box/top-up writes go through store.mutate() (fresh box —
 * grids inside it — auto-applied); ballot/waitlist writes apply their slice
 * responses via store.applyMutation(). No blanket refresh() after grid
 * mutations — the rebroadcast re-runs the resolver and the button repaints
 * (add → stepper → "In your box — manage").
 *
 * Box-line writes open the box drawer on success;
 * ballot/waitlist success keeps the in-place label flip.
 *
 * The join key is the Shopify PRODUCT ID:
 * sections/twc-pdp-shell.liquid publishes it as data-twc-pdp-product-id and
 * this element reads it from there (falling back to its own attribute only
 * when the buybox renders without the shell, e.g. the theme editor's section
 * preview). The composed dashboard payload carries `productIndex`, which maps
 * that id to the catalogue item id the grids key on.
 */
(function (global) {
  'use strict';

  if (!global.customElements || global.customElements.get('twc-pdp-buybox')) return;

  var DEFAULTS = {
    cta_ballot: 'Enter Ballot',
    cta_leave_ballot: 'Leave ballot',
    cta_waitlist: 'Join Waitlist',
    cta_leave_waitlist: 'Leave Waitlist',
    cta_add: 'Add to box',
    cta_buy: 'Buy today',
    cta_in_box: 'In your box — manage',
    cta_rejoin: 'Rejoin the Club',
    cta_fix_payment: 'Fix your payment',
    cta_guest: 'Sign up free now',
    button_pending: 'Working…',
    ballot_closed: 'This ballot is closed or full.',
    error_generic: 'Something went wrong. Please try again.',
    preview_note: 'Preview mode — actions are disabled here.',
    window_closed: 'The editing window for this month has closed.',
    unavailable: 'Not available right now',
  };

  function api() {
    return global.TwcAccountApi || null;
  }

  function store() {
    return global.TwcMemberData || null;
  }

  function resume() {
    return global.TwcResumeDialog || null;
  }

  function resolver() {
    return global.TwcCtaResolver || null;
  }

  var Buybox = function () {
    return Reflect.construct(HTMLElement, [], Buybox);
  };
  Buybox.prototype = Object.create(HTMLElement.prototype);
  Buybox.prototype.constructor = Buybox;

  Buybox.prototype.connectedCallback = function () {
    this._abort = new AbortController();
    var signal = this._abort.signal;

    this._i18n = this._readI18n();
    this._error = this.querySelector('[data-twc-buybox-error]');
    this._cta = this.querySelector('[data-twc-buybox-cta]');
    this._ctaLabel = this._cta ? this._cta.textContent.trim() : '';
    this._ctaArgs = null;
    this._busyOn = false;
    // Shared-dialog host slots (contract in twc-resume-dialog.js header).
    this._dialog = null;
    this._opener = null;

    this._initTabs(signal);
    this._initGallery(signal);
    this._initCta(signal);
    this._initMember();

    // A bfcache restore brings the frozen page back with the button still
    // mid-flight; release it.
    global.addEventListener(
      'pageshow',
      function (event) {
        if (event.persisted) this._busy(false);
      }.bind(this),
      { signal: signal }
    );
  };

  Buybox.prototype.disconnectedCallback = function () {
    var R = resume();
    if (R) R.closeConfirmDialog(this, true);
    if (this._abort) {
      this._abort.abort();
      this._abort = null;
    }
    if (this._unsub) {
      this._unsub();
      this._unsub = null;
    }
  };

  /* ---- copy ------------------------------------------------------------ */

  Buybox.prototype._readI18n = function () {
    var tag = this.querySelector('script[data-twc-i18n]');
    if (!tag) return {};
    try {
      var raw = JSON.parse(tag.textContent);
      // The `t` filter HTML-escapes, so section-emitted copy arrives with
      // entities; decode once here and render through textContent after.
      return api() && api().decodeDeep ? api().decodeDeep(raw) : raw;
    } catch (e) {
      return {};
    }
  };

  Buybox.prototype._t = function (key) {
    var v = this._i18n ? this._i18n[key] : null;
    return v == null || v === '' ? DEFAULTS[key] || '' : v;
  };

  // Raw per-product override passthroughs ("pre_sale", "sold_out_label"):
  // blank means "no override", never a default.
  Buybox.prototype._ovr = function (key) {
    var v = this._i18n ? this._i18n[key] : null;
    return v == null ? '' : String(v);
  };

  /* ---- 1. tabs --------------------------------------------------------- */

  Buybox.prototype._initTabs = function (signal) {
    var self = this;
    var tabs = Array.prototype.slice.call(this.querySelectorAll('[role="tab"]'));
    if (!tabs.length) return;
    this._tabs = tabs;

    tabs.forEach(function (tab) {
      tab.addEventListener(
        'click',
        function () {
          self._selectTab(tab);
        },
        { signal: signal }
      );
      tab.addEventListener(
        'keydown',
        function (event) {
          var i = tabs.indexOf(tab);
          var next = null;
          if (event.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
          else if (event.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
          else if (event.key === 'Home') next = tabs[0];
          else if (event.key === 'End') next = tabs[tabs.length - 1];
          if (!next) return;
          event.preventDefault();
          self._selectTab(next);
          next.focus();
        },
        { signal: signal }
      );
    });
  };

  Buybox.prototype._selectTab = function (tab) {
    var self = this;
    this._tabs.forEach(function (t) {
      var on = t === tab;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
      var panel = self.querySelector('#' + CSS.escape(t.getAttribute('aria-controls') || ''));
      if (panel) panel.hidden = !on;
    });
  };

  /* ---- 2. gallery ------------------------------------------------------ */

  Buybox.prototype._initGallery = function (signal) {
    var thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-twc-buybox-thumb]'));
    var hero = this.querySelector('[data-twc-buybox-hero]');
    if (!thumbs.length || !hero) return;

    thumbs.forEach(function (thumb) {
      thumb.addEventListener(
        'click',
        function () {
          thumbs.forEach(function (t) {
            t.setAttribute('aria-current', t === thumb ? 'true' : 'false');
          });
          // srcset first: setting src alone leaves the old srcset winning.
          hero.srcset = thumb.dataset.srcset || '';
          hero.src = thumb.dataset.full || hero.src;
          hero.alt = thumb.dataset.alt || hero.alt;
        },
        { signal: signal }
      );
    });
  };

  /* ---- CTA plumbing ---------------------------------------------------- */

  Buybox.prototype._initCta = function (signal) {
    var self = this;
    if (!this._cta || this._cta.tagName === 'A') return;

    this._cta.addEventListener(
      'click',
      function () {
        var action = self._cta.dataset.twcAction;
        if (action) self._dispatch(action);
      },
      { signal: signal }
    );
  };

  Buybox.prototype._showError = function (message) {
    if (!this._error) return;
    this._error.textContent = message || '';
    this._error.hidden = !message;
  };

  Buybox.prototype._busy = function (on) {
    this._busyOn = !!on;
    if (!this._cta) return;
    if (this._cta.tagName !== 'A') this._cta.disabled = !!on;
    this._cta.setAttribute('aria-busy', on ? 'true' : 'false');
    this._cta.textContent = on ? this._t('button_pending') : this._ctaLabel;
  };

  Buybox.prototype._setLabel = function (label, action, disabled) {
    if (!this._cta) return;
    this._ctaLabel = label;
    if (!this._busyOn) this._cta.textContent = label;
    if (action) this._cta.dataset.twcAction = action;
    if (this._cta.tagName !== 'A') this._cta.disabled = !!disabled;
    this._cta.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  };

  // Sub-status demotion: the button becomes a plain link (account or join).
  // Swapping the node drops its click listener with it — links navigate.
  Buybox.prototype._demoteToLink = function (href, label) {
    if (!this._cta) return;
    if (this._cta.tagName === 'A') {
      this._cta.setAttribute('href', href);
      this._setLabel(label, null, false);
      return;
    }
    var a = document.createElement('a');
    a.className = this._cta.className;
    a.setAttribute('data-twc-buybox-cta', '');
    a.setAttribute('href', href);
    a.textContent = label;
    this._cta.replaceWith(a);
    this._cta = a;
    this._ctaLabel = label;
    this._ctaArgs = null;
  };

  /* ---- 3. member state ------------------------------------------------- */

  Buybox.prototype._productId = function () {
    // The shell publishes the page-level join key; this element carries the
    // same value only as a standalone fallback (HANDOFF-P0.md §2).
    var shell = document.querySelector('[data-twc-pdp-product-id]');
    var fromShell = shell ? shell.getAttribute('data-twc-pdp-product-id') : '';
    return fromShell || this.dataset.productId || '';
  };

  Buybox.prototype._initMember = function () {
    var self = this;
    if (self.dataset.engine !== '1') return;
    var s = store();
    if (!s) return;

    // The Liquid rendered the button DISABLED — inert first paint, no click
    // to swallow. The subscription always resolves: even a failed load
    // arrives as loaded with no grids, which becomes "Not available right
    // now".
    self._unsub = s.subscribe(function (state) {
      self._applyMemberState(state);
    });
    s.load();
  };

  // The store has answered: retire the first-paint resolving dots. Runs
  // before every render path (resolver, demotion links, unavailable) and
  // no-ops on later broadcasts.
  Buybox.prototype._endResolving = function () {
    if (!this._cta) return;
    this._cta.classList.remove('twc-pdp-buybox__cta--resolving');
    this._cta.setAttribute('aria-busy', 'false');
  };

  Buybox.prototype._applyMemberState = function (state) {
    if (!state || !state.loaded) return;
    this._endResolving();

    var R = resolver();
    // No resolver module = no rules to apply. Keep the disabled first-paint
    // button rather than render a wrong one.
    if (!R) return;

    // Sub-status BEFORE grid resolution: a member who must first do something
    // else never reaches the resolver. The ladder itself is in the shared
    // resolver; the links and the words stay here.
    var gate = R.memberGate(state);
    if (gate) {
      if (gate.demote === 'guest') {
        // Not a member yet: same door as a guest, same copy (the state's
        // resolved guest label, shipped in the i18n blob).
        this._demoteToLink(this.dataset.joinUrl || '/pages/signup', this._t('cta_guest'));
        return;
      }
      var accountUrl = this.dataset.accountUrl || '/account';
      this._demoteToLink(accountUrl, this._t('cta_fix_payment'));
      return;
    }
    // active | paused | unsubscribed | gift_active_* fall through to the
    // resolver: paused and unsubscribed stay actionable (gating happens at
    // dispatch), gift members are active members whose engine rules answer 409
    // with their own words.

    this._renderCta(this._resolveCta(state, this._productId()));
  };

  // ONE uniform resolver over the server-composed grids (twc-cta-resolver.js).
  // The page's product id joins to a catalogue item id through the payload's
  // `productIndex` map, and ballots ride the grids like any other entry.
  Buybox.prototype._resolveCta = function (state, productId) {
    var R = resolver();
    return R ? R.resolveCta(state, productId) : null;
  };

  // An unavailable button must not keep a stale first-paint action: nothing
  // dispatches while it is disabled, but a stale value is a trap for any
  // future code that re-enables it.
  Buybox.prototype._clearAction = function () {
    if (this._cta && this._cta.dataset) delete this._cta.dataset.twcAction;
  };

  // The muted ghost treatment + the top-price rule ride the resolved state:
  // a member whose box is settled must not read a price over a dead button.
  // The Specs table price is DELIBERATELY left alone — it always shows.
  Buybox.prototype._setMuted = function (on) {
    if (this._cta) this._cta.classList.toggle('twc-pdp-buybox__cta--muted', !!on);
  };

  Buybox.prototype._setPriceVisible = function (on) {
    // Ghost (visibility), never remove: the line's box stays in flow so the
    // first-paint → resolved settle cannot shift the layout below it.
    var p = document.querySelector('.twc-pdp-buybox__price');
    if (p) p.classList.toggle('twc-pdp-buybox__price--ghost', !on);
  };

  // The label the shape asks for: a per-product override wins, then the
  // engine's own words, then the locale key.
  Buybox.prototype._label = function (p) {
    var override = p.overrideKey ? this._ovr(p.overrideKey) : '';
    return override || p.engineLabel || (p.labelKey ? this._t(p.labelKey) : '');
  };

  // §10 kind → button. The shape comes from the shared resolver; every DOM
  // write stays here.
  Buybox.prototype._renderCta = function (cta) {
    var R = resolver();
    if (!R) return;
    var p = R.presentation(cta);

    this._setMuted(p.muted);
    this._setPriceVisible(p.priceVisible);

    if (p.destination === 'account') {
      this._demoteToLink(this.dataset.accountUrl || '/pages/account-dashboard', this._label(p));
      return;
    }

    // Inert = no action and no args: a dead button must not keep a stale
    // first-paint action (no action exists for this member, the coming-soon
    // moment, or a kind this theme version does not know).
    if (p.inert) {
      this._ctaArgs = null;
      this._clearAction();
      this._setLabel(this._label(p), null, true);
      return;
    }

    this._ctaArgs = (cta && cta.args) || {};
    this._setLabel(this._label(p), p.action, p.disabled);
  };

  /* ---- dispatch -------------------------------------------------------- */

  // The ladder of the writes lives in assets/twc-cta-resolver.js §4, because
  // the product cards send the same requests. This element
  // is a host of that module: the three methods below are the full contract,
  // together with the _abort / _dialog / _opener slots of the resume dialog.
  // The re-entry guard stays here: this page has ONE control, thus one busy
  // flag answers for it.
  Buybox.prototype.twcBusy = function (on) {
    this._busy(on);
  };

  Buybox.prototype.twcError = function (message) {
    this._showError(message);
  };

  Buybox.prototype.twcText = function (key) {
    return this._t(key);
  };

  Buybox.prototype._dispatch = function (action) {
    var R = resolver();
    if (!R || this._busyOn) return;
    R.dispatch(this, action, this._ctaArgs);
  };

  global.customElements.define('twc-pdp-buybox', Buybox);
})(window);
